import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { useIsMounted } from '~/hooks';
import { colors } from '~/styles';

const Arrow = styled.div`
  position: absolute;
  border: solid 0.5rem transparent;
  z-index: 299;
`;

const HoverCardContent = styled.div`
  display: flex;
  flex-direction: column;
  width: ${({ $width }) => $width};
  align-items: center;
  border-radius: 0.3125rem;
  border: solid 1px ${colors.grey10};
  background-color: ${colors.white};
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
  z-index: 300;
  left: 0;
  position: relative;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  transition: opacity 50ms ease-out;

  &[data-placement='left'] > ${Arrow} {
    left: 100%;
    border-left-color: ${colors.white};
  }

  &[data-placement='right'] > ${Arrow} {
    right: 100%;
    border-right-color: ${colors.white};
  }

  &[data-placement='top'] > ${Arrow} {
    top: 100%;
    border-top-color: ${colors.white};
  }

  &[data-placement='bottom'] > ${Arrow} {
    bottom: 100%;
    border-bottom-color: ${colors.white};
  }
`;
function HoverCard({
  children,
  message,
  placement = 'bottom',
  offsetTop = 15,
  offsetLeft = 0,
  width = '16rem',
  ...containerProps
}) {
  const [visible, setVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);

  const isMounted = useIsMounted();
  const hovering = useRef(false);
  const forceVisible = useRef(false);
  const hoverHandler = useRef(null);

  // The arrow modifier keeps the arrow on the reference after preventOverflow slides the card to stay on screen.
  const modifiers = useMemo(
    () => [
      { name: 'offset', options: { offset: [offsetLeft, offsetTop] } },
      { name: 'arrow', options: { element: arrowElement, padding: 8 } },
    ],
    [offsetLeft, offsetTop, arrowElement],
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    strategy: 'fixed',
    modifiers,
  });

  // Browsers fire mouseleave only after a scroll settles, so a card would otherwise follow its row while it scrolls away.
  useEffect(() => {
    if (!visible) return;

    const hide = () => {
      forceVisible.current = false;
      hoverHandler.current = null;
      setVisible(false);
    };

    window.addEventListener('scroll', hide, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', hide, { capture: true });
  }, [visible]);

  function handleMouseEnter() {
    hovering.current = true;

    hoverHandler.current = () => {
      setVisible(true);
    };

    setTimeout(() => {
      if (hoverHandler.current && isMounted.current) hoverHandler.current();
      hoverHandler.current = null;
    }, 150);
  }

  function handleMouseLeave() {
    hovering.current = false;

    setTimeout(() => {
      if (!forceVisible.current) {
        setVisible(false);
      }
    }, 150);

    if (hoverHandler.current) {
      hoverHandler.current = null;
    }
  }

  function handleCardMouseEnter() {
    forceVisible.current = true;
  }

  function handleCardMouseLeave() {
    forceVisible.current = false;
    setVisible(false);
  }

  const placementValue = attributes.popper?.['data-popper-placement'];

  return (
    <>
      <div
        ref={setReferenceElement}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...containerProps}>
        {children}
      </div>

      {visible &&
        message &&
        ReactDOM.createPortal(
          <HoverCardContent
            ref={setPopperElement}
            $width={width}
            style={{ ...styles.popper }}
            {...attributes.popper}
            data-placement={placementValue}
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}>
            {message}
            <Arrow ref={setArrowElement} style={styles.arrow} />
          </HoverCardContent>,
          document.body,
        )}
    </>
  );
}

export default HoverCard;
